const symbolObservable = (() => typeof Symbol === 'function' && Symbol.observable || '@@observable')();
function toObserver(nextHandler, errorHandler, completionHandler) {
  const isObserver = typeof nextHandler === 'object';
  const self = isObserver ? nextHandler : undefined;
  return {
    next: (isObserver ? nextHandler.next : nextHandler)?.bind(self),
    error: (isObserver ? nextHandler.error : errorHandler)?.bind(self),
    complete: (isObserver ? nextHandler.complete : completionHandler)?.bind(self)
  };
}
function setter(context, recipe) {
  return recipe(context);
}
function createStoreCore(initialContext, transitions, updater) {
  let observers;
  const initialSnapshot = {
    context: initialContext,
    status: 'active',
    output: undefined,
    error: undefined
  };
  let currentSnapshot = initialSnapshot;
  const transition = createStoreTransition(transitions, updater);
  function receive(event) {
    currentSnapshot = transition(currentSnapshot, event);
    observers?.forEach(o => o.next?.(currentSnapshot));
  }
  const store = {
    send(event) {
      receive(event);
    },
    getSnapshot() {
      return currentSnapshot;
    },
    getInitialSnapshot() {
      return initialSnapshot;
    },
    subscribe(observerOrFn) {
      const observer = toObserver(observerOrFn);
      observers ??= new Set();
      observers.add(observer);
      return {
        unsubscribe() {
          return observers?.delete(observer);
        }
      };
    },
    [symbolObservable]() {
      return this;
    }
  };
  return store;
}

/**
 * Creates a **store** that has its own internal state and can be sent events that
 * update its internal state based on transitions.
 *
 * @example
  ```ts
  const store = createStore({
    // Initial context
    { count: 0 },
    // Transitions
    {
      on: {
        inc: (context, event: { by: number }) => {
          return {
            count: context.count + event.by
          }
        }
      }
    }
  });

  store.subscribe((snapshot) => {
    console.log(snapshot);
  });

  store.send({ type: 'inc', by: 5 });
  // Logs { context: { count: 5 }, status: 'active', ... }
  ```
 */
function createStore(initialContext, transitions) {
  return createStoreCore(initialContext, transitions);
}

/**
 * Creates a `Store` with a provided producer (such as Immer's `producer(…)`
 * A store has its own internal state and can receive events.
 *
 * @example
  ```ts
  import { produce } from 'immer';

  const store = createStoreWithProducer(produce, {
    // Initial context
    { count: 0 },
    // Transitions
    {
      on: {
        inc: (context, event: { by: number }) => {
          context.count += event.by;
        }
      }
    }
  });

  store.subscribe((snapshot) => {
    console.log(snapshot);
  });

  store.send({ type: 'inc', by: 5 });
  // Logs { context: { count: 5 }, status: 'active', ... }
  ```
 */
function createStoreWithProducer(producer, initialContext, transitions) {
  return createStoreCore(initialContext, transitions, producer);
}
/**
 * Creates a store function, which is a function that accepts the current snapshot and an event and returns a new snapshot.
 * @param transitions
 * @param updater
 * @returns
 */
function createStoreTransition(transitions, updater) {
  return (snapshot, event) => {
    let currentContext = snapshot.context;
    const assigner = transitions?.[event.type];
    if (!assigner) {
      return snapshot;
    }
    if (typeof assigner === 'function') {
      currentContext = updater ? updater(currentContext, draftContext => assigner?.(draftContext, event)) : setter(currentContext, draftContext => Object.assign({}, currentContext, assigner?.(draftContext, event // TODO: help me
      )));
    } else {
      const partialUpdate = {};
      for (const key of Object.keys(assigner)) {
        const propAssignment = assigner[key];
        partialUpdate[key] = typeof propAssignment === 'function' ? propAssignment(currentContext, event) : propAssignment;
      }
      currentContext = Object.assign({}, currentContext, partialUpdate);
    }
    return {
      ...snapshot,
      context: currentContext
    };
  };
}

/**
 * An actor logic creator which creates store [actor logic](https://stately.ai/docs/actors#actor-logic) for use with XState.
 *
 * @param initialContext The initial context for the store, either a function that returns context based on input, or the context itself
 * @param transitions The transitions object defining how the context updates due to events
 * @returns An actor logic creator function that creates store actor logic
 */
function fromStore(initialContext, transitions) {
  const transition = createStoreTransition(transitions);
  return {
    transition,
    start: () => {},
    getInitialSnapshot: (_, input) => {
      return {
        status: 'active',
        context: typeof initialContext === 'function' ? initialContext(input) : initialContext,
        output: undefined,
        error: undefined
      };
    },
    getPersistedSnapshot: s => s,
    restoreSnapshot: s => s
  };
}

export { createStore, createStoreWithProducer, fromStore };
